/**
 * Survivorship, shown rather than described.
 *
 * This is the most misunderstood idea in the product: everyone reads "the most
 * recent value wins" and agrees, and then cannot say what happens when the
 * newest record is from the least trusted system. So the panel stops asserting
 * and starts demonstrating — two invented records from two sources, and the
 * value that survives, recomputed the moment the strategy or the source order
 * changes.
 *
 * Deliberately not the tester below it. The tester is for a real question you
 * have; this is for the question you did not know to ask, and it costs no
 * typing, which is why it updates live instead of waiting for a Run.
 */

import { useMemo } from 'react'
import type { NodeTypeDetail, SurvivorshipRule } from '../../types/brain.types'

/** Two invented records, a day apart, from two different systems. */
const OLDER = { source: 'crm', when: 'yesterday' }
const NEWER = { source: 'marketing', when: 'today' }

export function WorkedExample({
  detail,
  rules,
}: {
  detail: NodeTypeDetail
  rules: SurvivorshipRule[]
}) {
  /** The property the example is told through: the first non-key text field. */
  const property = useMemo(() => {
    const candidates = detail.properties.filter(
      (p) => !p.is_primary_key && ['string', 'text', 'email'].includes(p.data_type),
    )
    return candidates[0] ?? detail.properties[0]
  }, [detail.properties])

  const rule = useMemo(() => {
    if (!property) return undefined
    return (
      rules.find((r) => r.property_key === property.key && r.active) ??
      rules.find((r) => r.property_key === '*' && r.active)
    )
  }, [rules, property])

  if (!property) return null

  const strategy = rule?.strategy ?? 'recency'
  const priority = rule?.source_priority ?? []
  const isOverride = rule?.property_key === property.key

  // The two values, and which one the rules as configured would keep.
  const older = { ...OLDER, value: `Acme Corporation` }
  const newer = { ...NEWER, value: `ACME CORP` }

  let winner: typeof older
  let because: string

  if (strategy === 'source_system') {
    const rank = (source: string) => {
      const index = priority.indexOf(source)
      return index === -1 ? priority.length : index
    }
    if (priority.length === 0) {
      winner = newer
      because = 'no source is ranked yet, so this falls back to the most recent record'
    } else if (rank(older.source) === rank(newer.source)) {
      winner = newer
      because = `neither ${older.source} nor ${newer.source} is ranked, so the more recent one wins`
    } else {
      winner = rank(older.source) < rank(newer.source) ? older : newer
      because = `${winner.source} is ranked ${rank(winner.source) + 1} of ${priority.length}`
    }
  } else {
    winner = newer
    because = 'it is the more recently observed record'
  }

  return (
    <div className="wx">
      <div className="wx-head">
        <h4>What this does to a real value</h4>
        <span className="small faint">
          {isOverride
            ? `the rule on ${property.key}`
            : rules.some((r) => r.property_key === '*')
              ? 'the default rule, applied to every property'
              : 'no rule yet — this is what the default would do'}
        </span>
      </div>

      <div className="wx-rows">
        {[older, newer].map((candidate) => {
          const won = candidate === winner
          return (
            <div className="wx-row" key={candidate.source} data-won={won || undefined}>
              <span className="wx-src mono">{candidate.source}</span>
              <span className="wx-when small faint">{candidate.when}</span>
              <span className="wx-val mono">{candidate.value}</span>
              <span className="wx-mark">{won ? 'survives' : 'discarded'}</span>
            </div>
          )
        })}
      </div>

      <p className="wx-because">
        <b className="mono">{property.key}</b> keeps <b className="mono">{winner.value}</b> —{' '}
        {because}.
      </p>
    </div>
  )
}
